import type { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IUserPasswordFormValues } from './UserPasswordForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useUpdateUserPasswordMutation } from '~/lib/api/modules/Users/hooks/useUpdateUserPasswordMutation';
import { useUserQuery } from '~/lib/api/modules/Users/hooks/useUserQuery';
import { UserPasswordForm } from './UserPasswordForm';

type IUserPasswordSectionProps = IContainerProps & {
  onSuccess?: () => void;
  cancelUrl?: string;
  userId: string;
};

export const UpdateUserPasswordForm: React.FC<IUserPasswordSectionProps> = (
  props,
) => {
  const { sx, onSuccess, cancelUrl, userId } = props;
  const { t } = useTranslation();
  const userQuery = useUserQuery({ userId });
  const updatePasswordMutation = useUpdateUserPasswordMutation();

  const handleSubmit: SubmitHandler<IUserPasswordFormValues> = (values) => {
    if (updatePasswordMutation.isLoading) {
      return;
    }

    return updatePasswordMutation.mutateAsync(
      {
        body: {
          oldPassword: values.currentPassword,
          password: values.newPassword,
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Title>
        {t('UpdateUserPasswordForm.change-your-password')}
      </ContentBlock.Title>

      <ContentBlock.Suspense
        loading={userQuery.isLoading}
        error={userQuery.error}
      >
        {userQuery.data ? (
          <UserPasswordForm
            mode="edition"
            onSubmit={handleSubmit}
            cancelUrl={cancelUrl}
          />
        ) : null}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
